/**
 * Paging dots for a scroll-snapping media track. Scrolling itself is handled by
 * CSS, so the element only builds the dots and keeps them in sync with the
 * track in both directions.
 *
 * Active index comes from scroll position rather than IntersectionObserver. On
 * the PDP the track stacks from lg up, so every slide would intersect the track
 * and the last dot would stay current after a resize back to the carousel.
 */
if (!customElements.get("media-carousel")) {
  customElements.define(
    "media-carousel",
    class MediaCarousel extends HTMLElement {
      connectedCallback() {
        const track = this.querySelector("[data-media-carousel-track]");
        const dotList = this.querySelector("[data-media-carousel-dots]");

        if (!track || !dotList) return;

        this.track = track;
        this.slides = Array.from(track.children);

        if (this.slides.length < 2) return;

        this.loop = this.hasAttribute("data-loop");
        this.multiView = this.hasAttribute("data-multi-view");
        if (this.loop) {
          // Invisible duplicates give a swipe past either end somewhere to land.
          const clone = (slide) => {
            const copy = slide.cloneNode(true);
            copy.setAttribute("data-carousel-clone", "");
            copy.setAttribute("aria-hidden", "true");
            copy.inert = true;
            copy.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
            return copy;
          };
          const beforeSources = this.multiView ? this.slides : this.slides.slice(-1);
          const afterSources = this.multiView ? this.slides : this.slides.slice(0, 1);
          this.loopCopies = [...beforeSources, ...afterSources].map((slide) => ({
            element: clone(slide), index: this.slides.indexOf(slide),
          }));
          const beforeCopies = this.loopCopies.slice(0, beforeSources.length);
          const afterCopies = this.loopCopies.slice(beforeSources.length);
          this.before = beforeCopies.at(-1).element;
          this.after = afterCopies[0].element;
          track.prepend(...beforeCopies.map((copy) => copy.element));
          track.append(...afterCopies.map((copy) => copy.element));
          track.scrollTo({ left: this.slides[0].offsetLeft, behavior: "instant" });
          this.onKeyDown = (event) => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const next = this.activeIndex + (event.key === "ArrowRight" ? 1 : -1);
            const slide = next < 0 ? this.before : next >= this.slides.length ? this.after : this.slides[next];
            track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
          };
          track.addEventListener("keydown", this.onKeyDown);
        }

        this.dots = this.slides.map((slide, index) =>
          this.createDot(slide, index),
        );
        dotList.replaceChildren(...this.dots);

        this.syncActiveDot = this.syncActiveDot.bind(this);
        this.track.addEventListener("scroll", this.syncActiveDot, {
          passive: true,
        });
        this.resizeObserver = new ResizeObserver(() => {
          if (this.multiView) {
            this.track.scrollTo({ left: this.slides[this.activeIndex || 0].offsetLeft, behavior: "instant" });
          }
          this.syncActiveDot();
        });
        this.resizeObserver.observe(this.track);
        this.syncActiveDot();
      }

      disconnectedCallback() {
        this.track?.removeEventListener("scroll", this.syncActiveDot);
        this.resizeObserver?.disconnect();
        clearTimeout(this.loopTimer);
        this.track?.removeEventListener("keydown", this.onKeyDown);
        this.loopCopies?.forEach((copy) => copy.element.remove());
      }

      createDot(slide, index) {
        const label = this.dataset.dotLabel || "%index%";
        const dot = document.createElement("button");

        dot.type = "button";
        const dotSize = this.dataset.dotSize === "large" ? "size-(--space-2)" : "size-1.5";
        dot.className =
          `${dotSize} cursor-pointer rounded-full border-none bg-current p-0 opacity-30 transition-opacity aria-[current=true]:opacity-100`;
        dot.setAttribute("aria-label", label.replace("%index%", index + 1));
        dot.addEventListener("click", () => {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        });

        return dot;
      }

      syncActiveDot() {
        const { scrollLeft } = this.track;
        let bestIndex = 0;
        let bestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - scrollLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });

        if (this.loop) {
          this.loopCopies.forEach((copy) => {
            const distance = Math.abs(copy.element.offsetLeft - scrollLeft);
            if (distance < bestDistance) {
              bestDistance = distance;
              bestIndex = copy.index;
            }
          });
          clearTimeout(this.loopTimer);
          this.loopTimer = setTimeout(() => {
            const copy = this.loopCopies.find((item) => Math.abs(item.element.offsetLeft - this.track.scrollLeft) < 2);
            if (copy) this.track.scrollTo({ left: this.slides[copy.index].offsetLeft, behavior: "instant" });
          }, 150);
        }
        this.activeIndex = bestIndex;
        this.setActiveDot(bestIndex);
      }

      setActiveDot(index) {
        this.dots.forEach((dot, dotIndex) => {
          if (dotIndex === index) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
      }
    },
  );
}
